import moment from 'moment'

import i18n from 'src/lib/localization/i18n'
import {
  LoanCustomPaymentIntention,
  LoanCustomPaymentOption,
  LoanPaymentStatusCode,
} from '@possible/cassandra/src/types/types.mobile.generated'
import {PaymentAction} from 'src/products/general/MakePayment/PaymentInput/PaymentInput.types'
import {
  convertPaymentIntentionStringToEnum,
  getLoanPaymentOptionLabel as getBaseLoanPaymentOptionLabel,
  getLoanPaymentOptionTrackingId as getBaseLoanPaymentOptionTrackingId,
} from 'src/products/loans/MakePayment/LoanPay.utils'
import {
  LoanCustomPaymentOptionWithMembershipOverdueAmount,
  LoanPayAmountEntryPayment,
} from 'src/products/loans/MakePayment/LoanPayAmountEntry/LoanPayAmountEntry.types'
import {
  MakePaymentAmountEntryInfoNoticeText,
  MakePaymentAmountEntryAlertData,
} from 'src/products/general/MakePayment/MakePaymentAmountEntry/MakePaymentAmountEntry.types'

/**
 * Get the button actions for the list of loan make payment options (Full Balance, Missed Installment, etc)
 * to be used with <PaymentInput /> component.
 */
export const getLoanPaymentOptionButtons = (args: {
  customPaymentOptions: LoanCustomPaymentOptionWithMembershipOverdueAmount[]
  selectedLoanCustomPaymentOption?: LoanCustomPaymentOptionWithMembershipOverdueAmount
}): {
  buttonActions: PaymentAction[] | undefined
  selectedButton: PaymentAction | undefined
} => {
  const {customPaymentOptions, selectedLoanCustomPaymentOption} = args

  const buttonActions: PaymentAction[] = customPaymentOptions.map((option) => ({
    amount: option.amount,
    label: getLoanPaymentOptionLabel(option),
    trackingId: getLoanPaymentOptionTrackingId(option),
  }))

  let selectedButton = buttonActions.find((action) => {
    const buttonPaymentIntention = convertPaymentIntentionStringToEnum(action.trackingId)
    return buttonPaymentIntention === selectedLoanCustomPaymentOption?.intention
  })

  // For OtherAmount, we need to ensure we create a button with the latest amount
  if (selectedLoanCustomPaymentOption?.intention === LoanCustomPaymentIntention.OtherAmount) {
    selectedButton = {
      amount: selectedLoanCustomPaymentOption.amount ?? '',
      label: getLoanPaymentOptionLabel(selectedLoanCustomPaymentOption),
      trackingId: getLoanPaymentOptionTrackingId(selectedLoanCustomPaymentOption),
    }
  }

  return {
    buttonActions,
    selectedButton,
  }
}

export const getInfoNoticeText = (args: {
  selectedButton?: PaymentAction
  payments: LoanPayAmountEntryPayment[]
}): MakePaymentAmountEntryInfoNoticeText => {
  const {selectedButton, payments} = args
  const hasMissedPayments =
    selectedButton?.trackingId === LoanCustomPaymentIntention.OverdueBalance.valueOf() ||
    selectedButton?.trackingId === LoanCustomPaymentIntention.MissedInstallment.valueOf()

  const upcomingPayment = payments.find(
    (payment) =>
      payment.statusCode === LoanPaymentStatusCode.Pending && parseFloat(payment.amount) > 0,
  )
  const paymentDate = moment(
    upcomingPayment?.rescheduledDate || upcomingPayment?.originalDate,
  ).format('dddd, MMMM Do')
  const key = hasMissedPayments
    ? 'MakePaymentAmountEntry:YourScheduledPaymentWithAmount'
    : 'MakePaymentAmountEntry:YourScheduledPayment'
  const data = hasMissedPayments
    ? {
        paymentDate: paymentDate,
        amount: upcomingPayment?.amount,
      }
    : {
        paymentDate: paymentDate,
      }
  return {
    data,
    key,
  }
}

/**
 * Get the default payment option to select.
 */
export const getDefaultLoanPaymentOption = (args: {
  customPaymentOptions: LoanCustomPaymentOptionWithMembershipOverdueAmount[]
}): LoanCustomPaymentOptionWithMembershipOverdueAmount | undefined => {
  const {customPaymentOptions} = args
  // sort payment options by lowest first
  const customPaymentOptionsSortedLowestFirst = customPaymentOptions
    ?.map((thisPaymentOption) => ({...thisPaymentOption}))
    ?.filter((item) => item.intention !== LoanCustomPaymentIntention.OtherAmount)
    .sort((a, b) => parseFloat(a.amount) - parseFloat(b.amount))

  // select a default payment option/intention as the lowest amount
  return customPaymentOptionsSortedLowestFirst?.[0]
}

export const getAlertAmount = (
  selectedAmount: string,
  paymentData: {
    amountOutstanding: string
    customPaymentOptions: LoanCustomPaymentOptionWithMembershipOverdueAmount[]
  },
): MakePaymentAmountEntryAlertData => {
  if (Number(selectedAmount) > Number(paymentData?.amountOutstanding)) {
    return {
      shouldShowAlert: true,
      alertTranslationKey: 'MakePaymentAmountEntry:OverpayAlertDescription',
      alertData: {
        amount: paymentData.amountOutstanding,
      },
    }
  }
  const minimumPaymentAmount =
    paymentData.customPaymentOptions.find(
      (option) => option.intention === LoanCustomPaymentIntention.OtherAmount,
    )?.amount ?? '5'
  if (Number(selectedAmount) < Number(minimumPaymentAmount)) {
    return {
      shouldShowAlert: true,
      alertTranslationKey: 'MakePaymentAmountEntry:UnderpayAlertDescription',
      alertData: {
        amount: minimumPaymentAmount,
      },
    }
  }
  return {shouldShowAlert: false, alertTranslationKey: undefined, alertData: undefined}
}

const isBaseLoanCustomPaymentOption = (
  option: LoanCustomPaymentOption | LoanCustomPaymentOptionWithMembershipOverdueAmount,
): option is LoanCustomPaymentOption => {
  return option.intention !== 'MEMBERSHIP_OVERDUE_AMOUNT'
}

export const getLoanPaymentOptionLabel = (
  paymentOption: LoanCustomPaymentOption | LoanCustomPaymentOptionWithMembershipOverdueAmount,
): string => {
  if (paymentOption.intention === 'MEMBERSHIP_OVERDUE_AMOUNT') {
    return i18n.t('MakePaymentAmountEntry:MembershipOverdueAmount')
  }
  if (isBaseLoanCustomPaymentOption(paymentOption)) {
    return getBaseLoanPaymentOptionLabel(paymentOption)
  }
  throw new Error('Unexpected payment option type')
}

export const getLoanPaymentOptionTrackingId = (
  paymentOption: LoanCustomPaymentOption | LoanCustomPaymentOptionWithMembershipOverdueAmount,
): string => {
  if (paymentOption.intention === 'MEMBERSHIP_OVERDUE_AMOUNT') {
    return 'MEMBERSHIP_OVERDUE_AMOUNT'
  }
  if (isBaseLoanCustomPaymentOption(paymentOption)) {
    return getBaseLoanPaymentOptionTrackingId(paymentOption)
  }
  throw new Error('Unexpected payment option type')
}

/**
 * Add the membership overdue amount to the list of custom payment options if it exists.
 */

export const addMembershipOverdueAmountIfPresent = (
  options: LoanCustomPaymentOptionWithMembershipOverdueAmount[],
  isAdvance: boolean,
  membershipOverdueAmount?: string,
): LoanCustomPaymentOptionWithMembershipOverdueAmount[] => {
  const hasMembershipOverdueAmount =
    isAdvance && membershipOverdueAmount && parseFloat(membershipOverdueAmount) > 0

  if (!hasMembershipOverdueAmount) {
    return options
  }

  return [
    ...options,
    {
      __typename: 'LoanCustomPaymentOption',
      intention: 'MEMBERSHIP_OVERDUE_AMOUNT' as const,
      amount: membershipOverdueAmount,
      installmentCount: 1,
    },
  ]
}
